import { removedMembersDetailsSchema, type GroupDetail } from '@split/shared';
import type { Mutation, MutationOptions, QueryClient } from '@tanstack/react-query';
import { api, isApiError } from '../../api/http';
import { mk, qk } from '../../api/keys';
import {
  expenseScope,
  receiptStillQueued,
  type CreateExpenseVars,
  type DeleteExpenseVars,
  type UpdateExpenseVars,
} from '../../api/queryClient';
import type { Translate } from '../../lib/i18n';
import type { expenseMessages } from '../../lib/i18n/messages/expenses';
import { dropQueuedReceipt } from '../../lib/receipts';

type ExpenseKey = keyof typeof expenseMessages.en;

/**
 * Runs one of the offline-capable expense mutations outside a component (e.g. from an Undo
 * toast after the form has unmounted). Uses the defaults registered in `queryClient.ts`.
 */
export function runMutation(
  client: QueryClient,
  key: readonly unknown[],
  vars: unknown,
  options: Pick<MutationOptions, 'scope' | 'meta'> = {},
): Promise<unknown> {
  return client
    .getMutationCache()
    .build(client, { ...options, mutationKey: key })
    .execute(vars);
}

export function invalidateGroup(client: QueryClient, groupId: string) {
  void client.invalidateQueries({ queryKey: qk.groupFeed(groupId) });
  void client.invalidateQueries({ queryKey: qk.groupBalances(groupId) });
  void client.invalidateQueries({ queryKey: qk.groups });
  void client.invalidateQueries({ queryKey: qk.overview });
  void client.invalidateQueries({ queryKey: qk.people });
}

type Outcome = 'success' | 'error' | 'paused' | 'removed';

/**
 * How a mutation run (e.g. a Retry from the feed) ends: `paused` once it waits offline,
 * `removed` once it is no longer in the cache (discarded).
 */
function settled(client: QueryClient, m: Mutation): Promise<Outcome> {
  const cache = client.getMutationCache();
  const outcome = (): Outcome | null =>
    !cache.getAll().includes(m)
      ? 'removed'
      : m.state.isPaused
        ? 'paused'
        : m.state.status === 'success' || m.state.status === 'error'
          ? m.state.status
          : null;
  return new Promise((resolve) => {
    const now = outcome();
    if (now) {
      resolve(now);
      return;
    }
    const unsubscribe = cache.subscribe(() => {
      const next = outcome();
      if (!next) return;
      unsubscribe();
      resolve(next);
    });
  });
}

/**
 * Undo for "Expense added": a create still waiting offline is simply dropped; otherwise the
 * expense is deleted once the create has gone through. A create that failed (`created` resolves
 * to `false`) is discarded instead, so it does not stay in the feed for a later Retry; if it was
 * retried from the feed meanwhile, that run decides.
 */
export function undoCreate(
  client: QueryClient,
  created: Promise<boolean>,
  vars: CreateExpenseVars,
) {
  const cache = client.getMutationCache();
  const find = () =>
    cache
      .findAll({ mutationKey: mk.createExpense })
      .find((m) => (m.state.variables as CreateExpenseVars | undefined)?.body.id === vars.body.id);
  const drop = (queued: Mutation) => {
    cache.remove(queued);
    void dropQueuedReceipt(vars.body.id);
    invalidateGroup(client, vars.groupId);
  };
  const queued = find();
  if (queued?.state.isPaused) {
    drop(queued);
    return;
  }
  void created.then(async (ok) => {
    if (!ok) {
      const failed = find();
      if (!failed) return;
      const outcome = await settled(client, failed);
      if (outcome === 'paused') drop(failed);
      if (outcome === 'error') discardFailedMutation(client, failed);
      if (outcome !== 'success') return;
    }
    return runMutation(
      client,
      mk.deleteExpense,
      {
        expenseId: vars.body.id,
        groupId: vars.groupId,
        title: vars.body.title,
      } satisfies DeleteExpenseVars,
      { scope: expenseScope(vars.body.id) },
    );
  });
}

/**
 * Names of the people who left the group whose balance an edit would change (422 with
 * `details.memberIds`), or `null` for any other error.
 */
export function leftMemberNames(
  client: QueryClient,
  groupId: string,
  e: unknown,
  fallback: string,
): string | null {
  if (!isApiError(e, 'VALIDATION_FAILED')) return null;
  const details = removedMembersDetailsSchema.safeParse(e.details);
  const ids = details.success ? details.data.memberIds : [];
  if (ids.length === 0) return null;
  const members = client.getQueryData<GroupDetail>(qk.group(groupId))?.members ?? [];
  return ids.map((id) => members.find((m) => m.id === id)?.name ?? fallback).join(', ');
}

/** `{ hasTitle, title }` for the messages that name an expense when its title is known. */
export function titleValues(title: string | null | undefined) {
  return title ? { hasTitle: 'yes', title } : { hasTitle: 'no', title: '' };
}

/**
 * The message for a delete or restore refused because it would change the balance of people who
 * left the group (same 422 `details.memberIds` as edits), or `null` for any other error.
 */
export function leftMembersMessage(
  client: QueryClient,
  t: Translate<ExpenseKey>,
  action: 'delete' | 'restore',
  vars: { groupId: string; title?: string | null },
  e: unknown,
): string | null {
  const names = leftMemberNames(client, vars.groupId, e, t('someone'));
  if (!names) return null;
  return t(action === 'delete' ? 'leftMembersDelete' : 'leftMembersRestore', {
    names,
    ...titleValues(vars.title),
  });
}

/** Undo for "Expense deleted" and the Restore of a deleted expense. */
export async function restoreExpense(client: QueryClient, expenseId: string, groupId: string) {
  try {
    await api('restoreExpense', { params: { id: expenseId } });
  } finally {
    // Also after a failure: whatever was shown meanwhile goes back to what the server has.
    void client.invalidateQueries({ queryKey: qk.expense(expenseId) });
    invalidateGroup(client, groupId);
  }
}

/**
 * Undo for "Expense deleted": a delete still waiting to run (offline) is simply dropped;
 * otherwise the expense is restored once the delete has gone through, so the delete can never
 * land after the restore. Nothing to restore when the delete failed (reported on its own).
 */
export function undoDelete(
  client: QueryClient,
  deleted: Promise<unknown>,
  vars: DeleteExpenseVars,
): Promise<void> {
  const cache = client.getMutationCache();
  const queued = cache
    .findAll({ mutationKey: mk.deleteExpense, status: 'pending' })
    .find(
      (m) => (m.state.variables as DeleteExpenseVars | undefined)?.expenseId === vars.expenseId,
    );
  if (queued?.state.isPaused) {
    cache.remove(queued);
    void client.invalidateQueries({ queryKey: qk.expense(vars.expenseId) });
    invalidateGroup(client, vars.groupId);
    return Promise.resolve();
  }
  return deleted.then(
    () => restoreExpense(client, vars.expenseId, vars.groupId),
    () => undefined,
  );
}

/**
 * Runs a failed create or queued edit again (its Retry), unless it was discarded or retried
 * meanwhile (e.g. a toast's Retry tapped after the feed's).
 */
export function retryMutation(client: QueryClient, m: Mutation) {
  if (!client.getMutationCache().getAll().includes(m) || m.state.status !== 'error') return;
  if (m.state.variables !== undefined) void m.execute(m.state.variables).catch(() => undefined);
}

/**
 * Drops a failed create or queued edit (its Discard), with its photo unless another queued
 * mutation still needs it; the expense goes back to what the server has.
 */
export function discardFailedMutation(client: QueryClient, m: Mutation) {
  client.getMutationCache().remove(m);
  const key = m.options.mutationKey?.[0];
  const vars = m.state.variables as CreateExpenseVars | UpdateExpenseVars | undefined;
  if (!vars) return;
  const expenseId =
    key === mk.createExpense[0]
      ? (vars as CreateExpenseVars).body.id
      : (vars as UpdateExpenseVars).expenseId;
  if (vars.hasReceipt === true && !receiptStillQueued(client, expenseId, undefined, true))
    void dropQueuedReceipt(expenseId);
  void client.invalidateQueries({ queryKey: qk.expense(expenseId) });
  invalidateGroup(client, vars.groupId);
}

const LAST_CURRENCY = 'split.lastCurrency.';
export function lastCurrency(groupId: string): string | null {
  try {
    return localStorage.getItem(LAST_CURRENCY + groupId);
  } catch {
    return null;
  }
}
export function rememberCurrencyForGroup(groupId: string, currency: string) {
  try {
    localStorage.setItem(LAST_CURRENCY + groupId, currency);
  } catch {
    // ignore
  }
}
